import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Card, CardContent, Fade } from '@mui/material';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { Header } from '../components/layout/Header';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import LandscapeIcon from '@mui/icons-material/Landscape';
import ScienceIcon from '@mui/icons-material/Science';
import { useAuth } from '../hooks/useAuth';

const features = [
  { icon: <AgricultureIcon sx={{ fontSize: 48 }} />, title: 'Gerenciamento do rebanho', desc: 'Cadastre, acompanhe e otimize seu rebanho com insights acionáveis.' },
  { icon: <LandscapeIcon sx={{ fontSize: 48 }} />, title: 'Gerenciamento de pastos', desc: 'Monitore a saúde dos pastos e planeje rotações eficientes.' },
  { icon: <ScienceIcon sx={{ fontSize: 48 }} />, title: 'Teste de mastite', desc: 'Realize testes e acompanhe indicadores de qualidade do leite.' },
];

const SubscriptionRequiredPage: React.FC = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [mounted, setMounted] = useState(false);
  const [featureIndex, setFeatureIndex] = useState(0);
  const [showFeature, setShowFeature] = useState(true);

  const TRANSITION_INTERVAL_MS = 4500; // tempo entre transições
  const FADE_DURATION_MS = 500; // duração do fade in/out

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      // fade out
      setShowFeature(false);
      const outTimer = setTimeout(() => {
        setFeatureIndex((prev) => (prev + 1) % features.length);
        // pequena pausa e fade in
        const inTimer = setTimeout(() => setShowFeature(true), 50);
        return () => clearTimeout(inTimer);
      }, FADE_DURATION_MS);
      return () => clearTimeout(outTimer);
    }, TRANSITION_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const handleGoToPlan = () => {
    const email = authState.user?.email ? `?email=${encodeURIComponent(authState.user.email)}` : '';
    navigate(`/plan-selection${email}`);
  };

  const currentFeature = features[featureIndex];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: DESIGN_TOKENS.colors.background.default }}>
      {/* Header igual ao da rota principal */}
      <Header progress={0} />
      {/* Top Section */}
      <Fade in={mounted} timeout={600}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', mt: '70px', px: { xs: 2, md: 3 } }}>
          <Card sx={{ maxWidth: 960, width: '100%', borderRadius: 4, boxShadow: '0 24px 48px rgba(0,0,0,0.12)' }}>
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <img src="/logo.svg" alt="MUU" style={{ height: 40, filter: 'brightness(0)' }} />
                <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>Sua plataforma de gestao com AI</Typography>
              </Box>
              {/* Logo posicionado estrategicamente (canto superior direito) */}
              <Box sx={{ position: 'relative' }}>
                <Box sx={{ position: 'absolute', top: -24, right: -12, opacity: 0.15 }}>
                  <img src="/logo.svg" alt="MUU marca" style={{ height: 96, filter: 'brightness(0)' }} />
                </Box>
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, color: DESIGN_TOKENS.colors.text.primary }}>
                Você não possui uma assinatura ativa no momento
              </Typography>
              <Typography variant="body1" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 3 }}>
                Assine o Plano MUU para acessar todo o gerenciamento da sua propriedade rural: rebanho, pastos,
                qualidade do leite, tarefas e muito mais.
              </Typography>
              <Button
                variant="contained"
                onClick={handleGoToPlan}
                sx={{ px: 3, py: 1.25, fontWeight: 700 }}
              >
                Adquirir Plano MUU
              </Button>
            </CardContent>
          </Card>
        </Box>
      </Fade>

      {/* Bottom Animated Feature Section */}
      <Box sx={{ mt: '10px', py: { xs: 3, md: 4 }, px: { xs: 4, md: 6 }, backgroundColor: DESIGN_TOKENS.colors.background.elevated }}>
        <Box sx={{ maxWidth: 960, mx: 'auto', textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary, mb: 2 }}>
            O que você pode fazer com MUU
          </Typography>
          <Box sx={{ position: 'relative', height: 160, overflow: 'hidden' }}>
            <Fade in={showFeature} timeout={{ enter: FADE_DURATION_MS, exit: FADE_DURATION_MS }}>
              <Box
                key={featureIndex}
                sx={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                  transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
                }}
              >
                <Box sx={{ color: DESIGN_TOKENS.colors.primary[600] }}>{currentFeature.icon}</Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}>
                  {currentFeature.title}
                </Typography>
                <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, maxWidth: 640 }}>
                  {currentFeature.desc}
                </Typography>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SubscriptionRequiredPage;
